// Base API client with authentication and error handling
import axios from 'axios';
import type { AxiosInstance, AxiosRequestConfig, AxiosResponse, AxiosError } from 'axios';
import type { ErrorResponse } from '../types/api';
import { auth } from '../config/firebase';

const API_BASE_URL = import.meta.env.VITE_API_URL || '/api/v1';

export class BaseApiClient {
  private client: AxiosInstance;
  private token: string | null = null;

  constructor() {
    // Load token from localStorage on initialization (fallback for legacy auth)
    this.token = localStorage.getItem('auth_token') || localStorage.getItem('firebaseToken');

    this.client = axios.create({
      baseURL: API_BASE_URL,
      timeout: 30000,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    this.setupInterceptors();
  }

  private setupInterceptors() {
    // Request interceptor for authentication
    this.client.interceptors.request.use(
      async (config) => {
        // Try to get Firebase token first
        const currentUser = auth.currentUser;
        if (currentUser) {
          try {
            const firebaseToken = await currentUser.getIdToken();
            config.headers.Authorization = `Bearer ${firebaseToken}`;
            return config;
          } catch (error) {
            console.error('Error getting Firebase token:', error);
          }
        }
        
        // Fallback to stored token
        if (this.token) {
          config.headers.Authorization = `Bearer ${this.token}`;
        }
        
        return config;
      },
      (error) => {
        return Promise.reject(error);
      }
    );

    // Response interceptor for error handling
    this.client.interceptors.response.use(
      (response: AxiosResponse) => {
        return response;
      },
      (error: AxiosError) => {
        this.handleApiError(error);
        return Promise.reject(error);
      }
    );
  }



  private handleApiError(error: AxiosError) {
    if (error.response) {
      const status = error.response.status;
      const data = error.response.data as ErrorResponse;

      switch (status) {
        case 401:
          // Handle unauthorized - redirect to login
          this.handleUnauthorized();
          break;
        case 403:
          // Handle forbidden
          console.error('Access forbidden:', data.detail);
          break;
        case 404:
          // Handle not found
          console.error('Resource not found:', data.detail);
          break;
        case 422:
          // Handle validation errors
          console.error('Validation error:', data.detail);
          break;
        case 500:
          // Handle server errors
          console.error('Server error:', data.detail);
          break;
        default:
          console.error('API error:', data.detail);
      }
    } else if (error.request) {
      // Network error
      console.error('Network error:', error.message);
    } else {
      // Other error
      console.error('Error:', error.message);
    }
  }

  private handleUnauthorized() {
    // Clear token and redirect to login
    this.setToken(null);
    // Only log once to avoid spam
    if (!this.hasLoggedUnauthorized) {
      console.log('User unauthorized, redirecting to login...');
      this.hasLoggedUnauthorized = true;
    }
  }

  private hasLoggedUnauthorized = false;

  public setToken(token: string | null) {
    this.token = token;
    if (token) {
      localStorage.setItem('auth_token', token);
    } else {
      localStorage.removeItem('auth_token');
    }
  }

  public getToken(): string | null {
    if (!this.token) {
      this.token = localStorage.getItem('auth_token');
    }
    return this.token;
  }

  public async get<T>(url: string, config?: AxiosRequestConfig): Promise<T> {
    const response = await this.client.get<T>(url, config);
    return response.data;
  }

  public async post<T>(url: string, data?: any, config?: AxiosRequestConfig): Promise<T> {
    console.log('📤 POST Request:', {
      url: `${this.client.defaults.baseURL}${url}`,
      data: url.includes('login') ? { email: data?.email, password: '***' } : data,
      headers: { ...this.client.defaults.headers, ...config?.headers },
      contentType: config?.headers?.['Content-Type'] || this.client.defaults.headers['Content-Type']
    });
    try {
      const response = await this.client.post<T>(url, data, config);
      console.log('📥 POST Response:', { status: response.status, data: response.data });
      return response.data;
    } catch (error: any) {
      if (error.response?.status === 422) {
        console.error('🚨 Validation Error Details:', error.response.data);
      } else if (error.response?.status === 401) {
        console.error('🚨 401 Error Details:', error.response.data);
      }
      throw error;
    }
  }

  public async put<T>(url: string, data?: any, config?: AxiosRequestConfig): Promise<T> {
    const response = await this.client.put<T>(url, data, config);
    return response.data;
  }

  public async patch<T>(url: string, data?: any, config?: AxiosRequestConfig): Promise<T> {
    const response = await this.client.patch<T>(url, data, config);
    return response.data;
  }

  public async delete<T>(url: string, config?: AxiosRequestConfig): Promise<T> {
    const response = await this.client.delete<T>(url, config);
    return response.data;
  }

  public async upload<T>(url: string, file: File, additionalData?: Record<string, any>): Promise<T> {
    const formData = new FormData();
    formData.append('file', file);

    if (additionalData) {
      Object.entries(additionalData).forEach(([key, value]) => {
        formData.append(key, value);
      });
    }

    const config: AxiosRequestConfig = {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    };

    const response = await this.client.post<T>(url, formData, config);
    return response.data;
  }
}

// Export singleton instance
export const baseClient = new BaseApiClient(); 